import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import Fuse from 'fuse.js';

/**
 * Custom hook that creates and manages a Fuse.js search instance for tweets
 * @param {Array} allTweets - Array of all tweet objects
 * @returns {Object} - Search engine functions and state
 */
const useSearchEngine = (allTweets) => {
  const [fuse, setFuse] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize Fuse instance when tweets are loaded
  useEffect(() => {
    if (allTweets && allTweets.length > 0) {
      const fuseInstance = new Fuse(allTweets, {
        includeScore: true,
        includeMatches: true,
        threshold: 0.0,
        ignoreLocation: true,
        useExtendedSearch: true,
        keys: [
          'text',
          'title',
          'user.name',
          'user.screen_name',
          'user.handle',
          'metadata.page_title'
        ]
      });
      setFuse(fuseInstance);
    }
  }, [allTweets]);

  // Extract namespace from Roam page title
  const extractNamespace = (pageTitle) => {
    if (!pageTitle) return null;
    
    // Handle nested namespaces like "Video/Show/Sherlock" or "Video/Movie/The Half of It"
    const parts = pageTitle.split('/');
    if (parts.length >= 2) {
      // For nested namespaces, take the most specific (last namespace part)
      // e.g., "Video/Show/Sherlock" -> "show", "Video/Movie/The Half of It" -> "movie"
      if (parts.length >= 3 && parts[0].toLowerCase() === 'video') {
        return parts[1].toLowerCase(); // "show" or "movie"
      }
      // For simple namespaces like "Book/Title" or "Article/Title"
      return parts[0].toLowerCase();
    }
    
    return null;
  };

  // Parse search query to extract operators and terms
  const parseSearchQuery = (query) => {
    const fromUsers = [];
    let remainingQuery = query;

    // Extract from:username operators
    const fromRegex = /\bfrom:(?:"([^"]+)"|(\S+))/gi;
    let match;

    while ((match = fromRegex.exec(query)) !== null) {
      const username = match[1] || match[2];
      fromUsers.push(username);
    }

    // Remove from: operators from the query
    if (fromUsers.length > 0) {
      remainingQuery = query.replace(/\bfrom:(?:"[^"]+"|[^\s]+)\s*/gi, '').trim();
    }

    // Extract other operators: is:, subreddit:, not:, namespace:, skip:, has:
    const operatorRegex = /\b([a-zA-Z]+):("[^"]+"|[^\s]+)/gi;
    let operatorMatch;
    const operators = {
      is: null,
      subreddit: null,
      not: null,
      namespace: null,
      skip: null,
      has: null
    };
    
    // Find all operator matches
    while ((operatorMatch = operatorRegex.exec(query)) !== null) {
      const [_, operator, value] = operatorMatch;
      if (operators.hasOwnProperty(operator.toLowerCase())) {
        // Remove quotes if present
        let cleanValue = value;
        if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
          cleanValue = cleanValue.slice(1, -1);
        }
        operators[operator.toLowerCase()] = cleanValue;
      }
    }
    
    // Remove operator expressions from query
    remainingQuery = remainingQuery.replace(/\b[a-zA-Z]+:("[^"]+"|[^\s]+)\s*/gi, '').trim();

    // Check if the remaining query is an exact match (quoted)
    const isExactMatch = remainingQuery.startsWith('"') && remainingQuery.endsWith('"');
    
    return {
      fromUsers,
      remainingQuery,
      isExactMatch,
      operators
    };
  };

  // Check if a tweet matches a user filter
  const userMatches = (tweet, users) => {
    if (!users || users.length === 0) return true;
    
    const screenName = (tweet.user?.screen_name || tweet.user?.handle || '').toLowerCase();
    const name = (tweet.user?.name || '').toLowerCase();

    return users.some(user => {
      const searchUser = user.toLowerCase();
      return screenName === searchUser || 
             (name && 
              name.split(' ').some(part => part.toLowerCase() === searchUser));
    });
  };

  // Check if a tweet matches a type filter
  const typeMatches = (tweet, operators) => {
    if (!operators.is) return true;
    
    const type = operators.is.toLowerCase();
    console.log(`Type check: ${type} for tweet:`, tweet);
    
    // For each potential type, check multiple properties in case the data structure varies
    if (type === 'reddit') {
      // Check source property
      if (tweet.source === 'reddit') {
        console.log('Reddit match by source property');
        return true;
      }
      
      // Check if it has Reddit-specific properties
      if (tweet.subreddit || tweet.subreddit_name_prefixed || 
          (tweet.permalink && tweet.permalink.includes('/r/')) ||
          (tweet.url && tweet.url.includes('reddit.com'))) {
        console.log('Reddit match by Reddit properties');
        return true;
      }
      
      console.log('Not a Reddit post');
      return false;
    }
    
    if (type === 'tweet') return ['like', 'bookmark', 'user_tweet'].includes(tweet.source);
    if (type === 'like') return tweet.source === 'like';
    if (type === 'bookmark') return tweet.source === 'bookmark';
    if (type === 'roam') return tweet.source === 'roam';
    
    return true;
  };
  
  // Check if a tweet matches a subreddit filter
  const subredditMatches = (tweet, operators) => {
    if (!operators.subreddit) return true;
    
    console.log('Subreddit match check for tweet:', tweet);
    
    // Return false if not a reddit post
    if (tweet.source !== 'reddit') {
      console.log('Not a reddit post');
      return false;
    }
    
    // Extract subreddit name from multiple possible locations
    let subredditName = '';
    
    // Check subreddit property (could be object or string)
    if (tweet.subreddit) {
      if (typeof tweet.subreddit === 'object') {
        subredditName = tweet.subreddit.display_name || tweet.subreddit.name || '';
        console.log('Found subreddit object:', tweet.subreddit);
      } else if (typeof tweet.subreddit === 'string') {
        subredditName = tweet.subreddit;
        console.log('Found subreddit string:', subredditName);
      }
    }
    
    // Check subreddit_name_prefixed if we didn't find a name yet
    if (!subredditName && tweet.subreddit_name_prefixed) {
      subredditName = tweet.subreddit_name_prefixed.replace(/^r\//, '');
      console.log('Found prefixed subreddit:', subredditName);
    }
    
    // If still not found, try additional known properties
    if (!subredditName) {
      // Try to extract from URL or permalink
      if (tweet.permalink) {
        const match = tweet.permalink.match(/\/r\/([^\/]+)\//i);
        if (match && match[1]) {
          subredditName = match[1];
          console.log('Extracted subreddit from permalink:', subredditName);
        }
      } else if (tweet.url) {
        const match = tweet.url.match(/\/r\/([^\/]+)\//i);
        if (match && match[1]) {
          subredditName = match[1];
          console.log('Extracted subreddit from URL:', subredditName);
        }
      }
    }
    
    // Final check for match
    if (subredditName) {
      const match = subredditName.toLowerCase() === operators.subreddit.toLowerCase();
      console.log(`Subreddit comparison: "${subredditName}" vs "${operators.subreddit}" = ${match}`);
      return match;
    }
    
    console.log('Could not find subreddit name in post');
    return false;
  };
  
  // Check if a tweet matches a NOT filter
  const notMatches = (tweet, operators) => {
    if (!operators.not) return true;
    
    const exclusionTerm = operators.not.toLowerCase();
    const text = (tweet.text || tweet.title || '').toLowerCase();
    
    return !text.includes(exclusionTerm);
  };

  // Check if a tweet matches a namespace filter
  const namespaceMatches = (tweet, operators) => {
    if (!operators.namespace) return true;

    // Only apply to Roam quotes
    if (tweet.source !== 'roam') return false;

    const pageTitle = tweet.metadata?.page_title;
    if (!pageTitle) return false;

    const tweetNamespace = extractNamespace(pageTitle);
    if (!tweetNamespace) return false;

    const filterNamespaces = operators.namespace.toLowerCase().split(',').map(ns => ns.trim());

    return filterNamespaces.includes(tweetNamespace);
  };

  // Check if a tweet matches a skip category filter
  const skipMatches = (tweet, operators) => {
    if (!operators.skip) return true;

    const skipFilter = operators.skip.toLowerCase();
    const tweetSkipCategories = tweet.skipCategories || [];

    // Handle special case: -skip or no-skip (exclude all skipped tweets)
    if (skipFilter === '-skip' || skipFilter === 'no-skip' || skipFilter === 'none') {
      return tweetSkipCategories.length === 0;
    }

    // Check if tweet has the specific skip category
    return tweetSkipCategories.map(cat => cat.toLowerCase()).includes(skipFilter);
  };

  // Check if a tweet matches a has: filter
  const hasMatches = (tweet, operators) => {
    if (!operators.has) return true;

    const hasFilter = operators.has.toLowerCase();

    // has:skip - tweet has ANY skip category
    if (hasFilter === 'skip' || hasFilter === 'skipped') {
      const tweetSkipCategories = tweet.skipCategories || [];
      return tweetSkipCategories.length > 0;
    }

    return true;
  };

  // Filter for exact matches
  const filterExactMatches = (results, query) => {
    const unquotedQuery = query.slice(1, -1);
    
    // Simple word boundary check without regex
    return results.filter(result => 
      ['text', 'title', 'user.name', 'user.screen_name'].some(key => {
        const value = key.includes('.') 
          ? result.item.user && result.item.user[key.split('.')[1]]
          : result.item[key];
        
        if (!value) return false;
        
        // Check if the value contains the exact word/phrase
        const lowerValue = value.toLowerCase();
        const lowerQuery = unquotedQuery.toLowerCase();
        
        // Check for word boundaries around the query
        const positions = [];
        let pos = lowerValue.indexOf(lowerQuery);
        
        while (pos !== -1) {
          positions.push(pos);
          pos = lowerValue.indexOf(lowerQuery, pos + 1);
        }
        
        return positions.some(position => {
          const beforeChar = position === 0 ? ' ' : lowerValue[position - 1];
          const afterChar = position + lowerQuery.length === lowerValue.length 
            ? ' ' 
            : lowerValue[position + lowerQuery.length];
          
          const beforeIsWordBoundary = /\W/.test(beforeChar) || beforeChar === ' ';
          const afterIsWordBoundary = /\W/.test(afterChar) || afterChar === ' ';
          
          return beforeIsWordBoundary && afterIsWordBoundary;
        });
      })
    );
  };

  // Store function dependencies in refs to avoid recreation
  const fuseRef = useRef(fuse);
  const allTweetsRef = useRef(allTweets);
  
  // Update refs when dependencies change
  useEffect(() => {
    fuseRef.current = fuse;
    allTweetsRef.current = allTweets;
  }, [fuse, allTweets]);
  
  // Perform search based on the query - using stable function reference
  const performSearch = useCallback((query) => {
    const currentFuse = fuseRef.current;
    const currentTweets = allTweetsRef.current;

    // Skip processing for partial operators to improve typing performance
    if (query.endsWith(':') && !query.includes(' ')) {
      // User is still typing an operator, don't process yet
      return;
    }

    if (!currentFuse || !query.trim()) {
      setSearchResults(null);
      return;
    }

    const { fromUsers, remainingQuery, isExactMatch, operators } = parseSearchQuery(query.trim());
    
    // Debug logging for operator parsing
    // console.log('Parsed search query:', {
    //   fromUsers,
    //   remainingQuery,
    //   isExactMatch,
    //   operators
    // });

    // If we have any operators but no text query, apply filters to all tweets
    if ((fromUsers.length > 0 || operators.is || operators.subreddit || operators.not || operators.namespace || operators.skip || operators.has) && !remainingQuery) {
      const filteredResults = currentTweets
        .filter(tweet => {
          const user = userMatches(tweet, fromUsers);
          const type = typeMatches(tweet, operators);
          const subreddit = subredditMatches(tweet, operators);
          const not = notMatches(tweet, operators);
          const namespace = namespaceMatches(tweet, operators);
          const skip = skipMatches(tweet, operators);
          const has = hasMatches(tweet, operators);

          return user && type && subreddit && not && namespace && skip && has;
        })
        .map((item, refIndex) => ({ item, score: 0, refIndex }));

      setSearchResults(filteredResults);
      return;
    }

    // Regular search with Fuse
    let results;

    if (isExactMatch) {
      const unquotedQuery = remainingQuery.slice(1, -1);
      results = currentFuse.search(unquotedQuery);
      results = filterExactMatches(results, remainingQuery);
    } else {
      results = currentFuse.search(remainingQuery);
    }

    // Apply all filters to search results
    if (fromUsers.length > 0 || operators.is || operators.subreddit || operators.not || operators.namespace || operators.skip || operators.has) {
      results = results.filter(result => {
        const user = userMatches(result.item, fromUsers);
        const type = typeMatches(result.item, operators);
        const subreddit = subredditMatches(result.item, operators);
        const not = notMatches(result.item, operators);
        const namespace = namespaceMatches(result.item, operators);
        const skip = skipMatches(result.item, operators);
        const has = hasMatches(result.item, operators);

        return user && type && subreddit && not && namespace && skip && has;
      });
    }

    setSearchResults(results);
  }, []); // Empty dependency array to maintain stable function reference

  // Generate a user-friendly display query
  const getDisplayQuery = (query) => {
    const { fromUsers, remainingQuery, isExactMatch } = parseSearchQuery(query.trim());

    // Special handling for user-only queries
    if (fromUsers.length > 0 && !remainingQuery) {
      if (fromUsers.length === 1) {
        return `all saves from ${fromUsers[0]}`;
      } else if (fromUsers.length === 2) {
        return `all saves from ${fromUsers[0]} or ${fromUsers[1]}`;
      } else {
        const lastUser = fromUsers[fromUsers.length - 1];
        const otherUsers = fromUsers.slice(0, -1);
        return `all saves from ${otherUsers.join(', ')} or ${lastUser}`;
      }
    }

    return isExactMatch ? remainingQuery.slice(1, -1) : remainingQuery;
  };

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    performSearch,
    getDisplayQuery,
    parseSearchQuery,
    isReady: !!fuse // Expose whether Fuse is initialized
  };
};

export default useSearchEngine;